/**
 * Перевод времени в положение на оси и обратно.
 *
 * Библиотека умеет переводить время в координату только для баров,
 * которые есть в ряду. Разметке этого мало: линия может уходить вправо
 * за последний бар, а её левый конец — влево за подгруженную историю.
 * Поэтому время сначала превращается в логический индекс — дробный и не
 * ограниченный длиной ряда, — а уже его библиотека кладёт на экран.
 *
 * Внутри ряда индекс ищется двоичным поиском по реальным барам, а не
 * делением на длину бара: Core не заполняет разрывы, и после пропуска
 * арифметика по времени разошлась бы с настоящими индексами.
 */

import type { Candle } from '../state/store.js';

/**
 * Время → дробный логический индекс.
 *
 * За пределами ряда индекс продолжается по шагу таймфрейма: другого
 * способа разместить точку там, где баров ещё нет, не существует.
 */
export function timeToLogical(
  candles: readonly Candle[],
  step: number,
  time: number,
): number {
  const first = candles[0];
  const last = candles[candles.length - 1];
  if (!first || !last) return 0;

  if (time <= first.time) return (time - first.time) / step;
  if (time >= last.time) return candles.length - 1 + (time - last.time) / step;

  let low = 0;
  let high = candles.length - 1;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (candles[mid]!.time <= time) low = mid;
    else high = mid;
  }

  const from = candles[low]!.time;
  const to = candles[high]!.time;
  // Доля внутри бара: клик приходится не только на границы.
  return to === from ? low : low + (time - from) / (to - from);
}

/**
 * Левый край плашки, которая целиком помещается на холсте.
 *
 * Подпись у фигуры возле края графика уходила за него и обрезалась:
 * «+5.00 (5.13%) · 3…» вместо числа баров. Ширина холста берётся из
 * самого контекста — в пространстве медиа-пикселей его масштаб равен
 * плотности экрана.
 */
export function onCanvasLeft(ctx: CanvasRenderingContext2D, left: number, width: number, margin = 4): number {
  const scale = ctx.getTransform().a || 1;
  const canvasWidth = ctx.canvas.width / scale;
  return Math.max(margin, Math.min(left, canvasWidth - width - margin));
}

/** Верхний край плашки, которая целиком помещается на холсте по высоте. */
export function onCanvasTop(ctx: CanvasRenderingContext2D, top: number, height: number, margin = 4): number {
  const scale = ctx.getTransform().d || 1;
  const canvasHeight = ctx.canvas.height / scale;
  return Math.max(margin, Math.min(top, canvasHeight - height - margin));
}

/**
 * Логическая позиция → пиксель, с долей между барами.
 *
 * Библиотека графика переводит в пиксели только целые позиции: на
 * дробную `logicalToCoordinate(696.5)` отвечает нулём — левым краем
 * холста. Всё, что стоит между барами, улетало влево: американская
 * сессия с 13:30 на часовом графике начиналась с края экрана, а линия,
 * нарисованная на 15м и открытая на 1ч, прыгала туда же. Здесь доля
 * считается между соседними целыми позициями.
 */
export function logicalX(toCoordinate: (logical: number) => number | null, logical: number): number | null {
  const base = Math.floor(logical);
  const left = toCoordinate(base);
  if (left === null) return null;
  const share = logical - base;
  if (share === 0) return left;
  const right = toCoordinate(base + 1);
  return right === null ? left : left + (right - left) * share;
}

/** Логический индекс → время. Обратная операция для клика по графику. */
export function logicalToTime(
  candles: readonly Candle[],
  step: number,
  logical: number,
): number {
  const first = candles[0];
  const last = candles[candles.length - 1];
  if (!first || !last) return 0;

  if (logical <= 0) return first.time + logical * step;
  if (logical >= candles.length - 1) {
    return last.time + (logical - (candles.length - 1)) * step;
  }

  const index = Math.floor(logical);
  const from = candles[index]!.time;
  const to = candles[index + 1]!.time;
  return from + (logical - index) * (to - from);
}

/**
 * Ближайшая цена бара — для магнита.
 *
 * Притягиваем к открытию, максимуму, минимуму и закрытию: это те цены,
 * по которым действительно шли сделки. Уровень, поставленный «примерно
 * по хвосту», обычно и означает «по хвосту».
 */
export function snapToBar(candle: Candle, price: number): number {
  const levels = [candle.open, candle.high, candle.low, candle.close];
  let best = levels[0]!;
  let bestDistance = Math.abs(price - best);

  for (const level of levels) {
    const distance = Math.abs(price - level);
    if (distance < bestDistance) {
      best = level;
      bestDistance = distance;
    }
  }
  return best;
}

/** Бар, ближайший к моменту времени. */
export function nearestCandle(candles: readonly Candle[], time: number): Candle | null {
  if (candles.length === 0) return null;
  const logical = Math.round(
    timeToLogical(candles, candles.length > 1 ? candles[1]!.time - candles[0]!.time : 1, time),
  );
  const index = Math.min(candles.length - 1, Math.max(0, logical));
  return candles[index] ?? null;
}

/** Расстояние от точки до отрезка — для попадания курсором. */
export function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) return Math.hypot(px - x1, py - y1);

  // Проекция точки на отрезок, зажатая его концами.
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSquared));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}
